import { createSlice } from "@reduxjs/toolkit";

const loadInitialOrderState = () => {
    try {
        const savedOrders = localStorage.getItem('orders');
        return savedOrders ? JSON.parse(savedOrders) : {
            currentOrder: [],
            orderHistory: [],
            totalAmount: 0
        };
    } catch (error) {
        console.error('Error loading orders from localStorage:', error);
        return {
            currentOrder: [],
            orderHistory: [],
            totalAmount: 0
        };
    }
};

const orderSlice = createSlice({
    name: "order",
    initialState: loadInitialOrderState(),
    reducers: {
        addToOrder: (state, action) => {
            const { item, quantity } = action.payload;
            if (!item || !item.id) {
                console.error('Invalid item data:', item);
                return;
            }

            // Create a serializable version of the item
            const serializableItem = {
                id: item.id,
                title: item.title,
                originalPrice: item.price,  // Store the original price
                price: item.price,  // Use the passed price (which should already be discounted)
                description: item.description,
                discountDetail: item.discountDetail ? {
                    discountPercentage: item.discountDetail.discountPercentage
                } : null,
                // Store image URL instead of function
                imageUrl: item.cloudR2ImagePath ? item.cloudR2ImagePath.getCDNUrl() : null
            };

            const existingItem = state.currentOrder.find(orderItem => orderItem.id === serializableItem.id);

            if (existingItem) {
                existingItem.quantity = (existingItem.quantity || 0) + quantity;
                existingItem.totalPrice = serializableItem.price * existingItem.quantity;
                console.log(`Updated quantity for item ID: ${serializableItem.id}, New Quantity: ${existingItem.quantity}`);
            } else {
                state.currentOrder.push({
                    ...serializableItem,
                    quantity: quantity,
                    totalPrice: serializableItem.price * quantity,
                    orderedAt: new Date().toISOString()
                });
                console.log(`Added new item to order:`, serializableItem);
            }

            // Update total amount
            state.totalAmount = state.currentOrder.reduce(
                (sum, item) => sum + item.totalPrice,
                0
            );

            try {
                localStorage.setItem('orders', JSON.stringify(state));
                console.log('Successfully saved to localStorage:', state);
            } catch (error) {
                console.error('Error saving orders to localStorage:', error);
            }
        },

        updateOrderQuantity: (state, action) => {
            const { itemId, quantity } = action.payload;
            if (!itemId || quantity < 0) {
                console.error('Invalid update data:', action.payload);
                return;
            }

            const item = state.currentOrder.find(orderItem => orderItem.id === itemId);

            if (item) {
                // Handle quantity update
                if (quantity === 0) {
                    // Remove item if quantity is 0
                    state.currentOrder = state.currentOrder.filter(orderItem => orderItem.id !== itemId);
                    console.log(`Removed item with ID: ${itemId} due to zero quantity`);
                } else {
                    // Update quantity and recalculate price
                    item.quantity = quantity;
                    item.totalPrice = item.price * quantity;
                    console.log(`Updated quantity for item ID: ${itemId}, New Quantity: ${quantity}`);
                }

                // Update total amount
                state.totalAmount = state.currentOrder.reduce(
                    (sum, item) => sum + item.totalPrice,
                    0
                );

                try {
                    localStorage.setItem('orders', JSON.stringify(state));
                    console.log('Successfully saved updated order to localStorage');
                } catch (error) {
                    console.error('Error saving orders to localStorage:', error);
                }
            } else {
                console.error(`Item with ID: ${itemId} not found in order`);
            }
        },
        removeFromOrder: (state, action) => {
            const { itemId } = action.payload;
            if (!itemId) {
                console.error('Invalid item ID for removal');
                return;
            }

            // Remove the item from current order
            state.currentOrder = state.currentOrder.filter(item => item.id !== itemId);
            console.log(`Removed item with ID: ${itemId} from order`);

            // Update total amount
            state.totalAmount = state.currentOrder.reduce(
                (sum, item) => sum + item.totalPrice,
                0
            );

            try {
                localStorage.setItem('orders', JSON.stringify(state));
                console.log('Successfully saved updated order to localStorage');
            } catch (error) {
                console.error('Error saving orders to localStorage:', error);
            }
        },
        confirmOrder: (state) => {
            if (state.currentOrder.length === 0) {
                console.log('No items in current order to confirm');
                return;
            }

            // Create a new confirmed order
            const confirmedOrder = {
                orderId: `ORD-${Date.now()}`,
                items: [...state.currentOrder],
                totalAmount: state.totalAmount,
                orderDate: new Date().toISOString(),
                status: 'confirmed'
            };

            // Add to order history and clear current order
            state.orderHistory.push(confirmedOrder);
            state.currentOrder = [];
            state.totalAmount = 0;

            try {
                localStorage.setItem('orders', JSON.stringify(state));
                console.log('Successfully saved confirmed order to localStorage');
            } catch (error) {
                console.error('Error saving confirmed order to localStorage:', error);
            }
        },

        clearCurrentOrder: (state) => {
            state.currentOrder = [];
            state.totalAmount = 0;

            try {
                localStorage.setItem('orders', JSON.stringify(state));
                console.log('Successfully cleared current order in localStorage');
            } catch (error) {
                console.error('Error saving cleared order to localStorage:', error);
            }
        }
    }
});

export const {
    addToOrder,
    removeFromOrder,
    updateOrderQuantity,
    confirmOrder,
    clearCurrentOrder
} = orderSlice.actions;

export default orderSlice.reducer;